import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Helmet } from 'react-helmet';
import { uniqBy } from 'lodash';
import qs from 'qs';

import { hasSubmitSucceeded, getFormValues } from 'redux-form';

import { withStyles } from '@material-ui/core/styles';

import Layout from 'components/common/Layout';
import Progress from 'components/common/Progress';
import Prompt from 'components/common/Prompt';
import RolePermissionsCreateEditForm from 'components/settings/RolePermissionsCreateEditForm';
import Button from 'components/common/Button';
import TabNavigation from 'components/common/TabNavigation';

import { appTitle, titleSeparator } from 'config/helmetSettings';

import { fetchRole, fetchRoles } from 'redux/modules/roles';
import { fetchAccessPermissions } from 'redux/modules/accessPermissions';

import objectDiff from 'utils/objectDiff';

const styles = () => ({
  toolbar: {
    marginBottom: 16,
  },
});

class ReportCreateEdit extends Component {
  componentDidMount() {
    const { variant, actions, location, match } = this.props;
    actions.fetchAccessPermissions({
      triggerLoading: true,
    });
    if (variant === 'edit') {
      actions.fetchRole({
        triggerLoading: true,
        ID: match.params.id,
      });
    }
    if (variant === 'copy') {
      const params = qs.parse(location.search, {
        ignoreQueryPrefix: true,
        parseArrays: true,
      });
      actions.fetchRoles({
        limit: 100,
        offset: 0,
        filters: [{
          name: 'ID',
          operation: 'in',
          val: params.ID.map(item => Number(item)),
        }],
        triggerLoading: true,
      });
    }
  }

  componentWillReceiveProps(nextProps) {
    const { variant, loading, history } = this.props;
    if (variant === 'edit') {
      if (loading > 0
        && nextProps.loading === 0
        && (!nextProps.roles[0] || !nextProps.roles[0].ID)) {
        history.replace('/settings/roles');
      }
    }
  }

  render() {
    const {
      roles,
      accessPermissions,
      variant,
      loading,
      submitSucceeded,
      formValues,
      initialValues,
      history,
      location,
      classes,
    } = this.props;
    const diffValues = objectDiff(formValues, initialValues);
    let rolesID;
    if (variant === 'copy') {
      const params = qs.parse(location.search, {
        ignoreQueryPrefix: true,
        parseArrays: true,
      });
      rolesID = params.ID.map(item => Number(item));
    }
    return (
      <Layout variant="settings">
        <TabNavigation
          items={[
            {
              text: 'Users',
              href: '/settings/users',
            },
            {
              text: 'Roles and Permissions',
              href: '/settings/roles',
            },
          ]}
        />
        {(loading > 0 || (variant === 'edit' && (!roles || !roles[0]))) ? (
          <Progress />
        ) : (
          <Fragment>
            <Helmet>
              <title>
                {appTitle} {titleSeparator} {' '}
                {variant === 'create' ? 'Create Role' : ''}
                {variant === 'copy' ? 'Model Role' : ''}
                {variant === 'edit' && roles[0] ? roles[0].Name : ''}
              </title>
            </Helmet>
            <RolePermissionsCreateEditForm
              {...variant === 'create' ? { form: 'roleCreate' } : {}}
              {...variant === 'edit' ? { form: `${roles[0].ID}-roleEdit` } : {}}
              {...variant === 'copy' ? { form: `${rolesID.join('-')}-roleCopy` } : {}}
              variant={variant}
              accessPermissions={accessPermissions}
              initialValues={initialValues}
              onSubmitSuccess={() => {
                history.push('/settings/roles');
              }}
            />
          </Fragment>
        )}
        <Prompt
          when={Object.keys(diffValues).length > 0 && !submitSucceeded}
          message="All the setups will be reset. Are you sure that you want to leave this page?"
        />
      </Layout>
    );
  }
}

ReportCreateEdit.propTypes = {
  roles: PropTypes.arrayOf(PropTypes.shape({
    ID: PropTypes.number,
  })),
  accessPermissions: PropTypes.arrayOf(PropTypes.shape({
    ID: PropTypes.number,
  })).isRequired,
  variant: PropTypes.oneOf(['create', 'edit', 'copy']),
  loading: PropTypes.number.isRequired,
  history: PropTypes.shape({
    push: PropTypes.func.isRequired,
  }).isRequired,
  match: PropTypes.shape({
    params: PropTypes.shape({
      id: PropTypes.string,
    }).isRequired,
  }).isRequired,
  actions: PropTypes.shape({
    fetchRole: PropTypes.func.isRequired,
    fetchAccessPermissions: PropTypes.func.isRequired,
  }).isRequired,
  submitSucceeded: PropTypes.bool.isRequired,
  formValues: PropTypes.shape({
    ID: PropTypes.number,
  }),
  initialValues: PropTypes.shape({
    ID: PropTypes.number,
  }),
  location: PropTypes.shape({
    search: PropTypes.string,
  }).isRequired,
  classes: PropTypes.shape({
    toolbar: PropTypes.string.isRequired,
  }).isRequired,
};

ReportCreateEdit.defaultProps = {
  roles: null,
  variant: 'create',
  formValues: {},
  initialValues: {},
};

const mapStateToProps = (store, ownProps) => {
  let formName = 'roleCreate';
  let roles;
  let combinedAccesses;
  if (ownProps.variant === 'edit') {
    roles = store.roles.data.length > 0
      ? store.roles.data.filter(item => item.ID === Number(ownProps.match.params.id)) : null;
    formName = roles && roles[0] ? `${roles[0].ID}-roleEdit` : 'roleEdit';
  }
  if (ownProps.variant === 'copy') {
    const params = qs.parse(ownProps.location.search, {
      ignoreQueryPrefix: true,
      parseArrays: true,
    });
    const rolesID = params.ID.map(item => Number(item));
    roles = store.roles.data.length > 0
      ? store.roles.data.filter(item => rolesID.indexOf(item.ID) !== -1) : null;
    formName = roles && roles.length > 0 ? `${rolesID.join('-')}-roleCopy` : 'roleCopy';
    combinedAccesses = roles && roles.length > 0
      ? uniqBy(roles.reduce((accumulator, currentValue) => ([
        ...accumulator,
        ...currentValue.Accesses,
      ]), []), 'ID') : [];
  }
  const formValues = getFormValues(formName)(store);
  const accessPermissions = store.accessPermissions.data;
  return {
    roles,
    accessPermissions,
    loading: store.roles.loading + store.accessPermissions.loading,
    submitSucceeded: hasSubmitSucceeded(formName)(store),
    formValues,
    ...ownProps.variant === 'create' ? { initialValues: {
      Access: {
        ...accessPermissions.reduce((accumulator, currentValue) => ({
          ...accumulator,
          [`ID-${currentValue.ID}`]: false,
        }), {}),
      },
    } } : {},
    ...(ownProps.variant === 'edit' && roles && roles[0]) ? { initialValues: {
      ID: roles[0].ID,
      Name: roles[0].Name,
      Description: roles[0].Description,
      'Full Access': accessPermissions.length === roles[0].Accesses.length,
      Access: {
        ...accessPermissions.reduce((accumulator, currentValue) => ({
          ...accumulator,
          [`ID-${currentValue.ID}`]:
            roles[0].Accesses.find(item => item.ID === currentValue.ID) !== undefined,
        }), {}),
      },
    } } : {},
    ...(ownProps.variant === 'copy' && roles && roles.length > 0) ? { initialValues: {
      Name: `${roles.map(item => item.Name).join(' ')} Copy`,
      'Full Access': accessPermissions.length === combinedAccesses.length,
      Access: {
        ...accessPermissions.reduce((accumulator, currentValue) => ({
          ...accumulator,
          [`ID-${currentValue.ID}`]:
            combinedAccesses.find(item => item.ID === currentValue.ID) !== undefined,
        }), {}),
      },
    } } : {},
  };
};

const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators({
    fetchRole,
    fetchRoles,
    fetchAccessPermissions,
  }, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(
  withStyles(styles)(ReportCreateEdit),
);



// WEBPACK FOOTER //
// ./src/pages/settings/RoleCreateEdit.js